import { useCallback, useEffect, useRef, useState } from "react";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import {
	ApiStatusError,
	copyHomeEntriesToSharedVolume,
	existingSharedVolumePaths,
	waitForSharedVolumeHomeCopy,
	type SharedVolumeExistingPath,
} from "@/lib/api";
import { ensureCloudSharedVolumeFolder } from "@/lib/cloud-shared-volume-entries";
import {
	cloudSharedVolumeDropDirectory,
	type CloudSharedVolumeEntryRow,
} from "@/lib/cloud-shared-volumes";
import {
	deviceSharedVolumeDropDirectory,
	type DeviceSharedVolumeEntryRow,
} from "@/lib/device-shared-volumes";
import type { EnvironmentEntryPayload } from "@/lib/dnd/payload";
import {
	resolveUploadItemPath,
	uploadDroppedFilesToEnvironment,
	uploadFilesToActiveEnvironment,
	type EnvironmentUploadOptions,
} from "@/lib/environment-files";
import { getTopLevelEnvironmentPaths } from "@/lib/environment-path-set";
import { withLoadingToast } from "@/lib/loading-toast";
import { useHasPermission } from "@/lib/permissions";
import { reportSharedVolumeFailure } from "@/lib/shared-volume-failure";
import { sharedVolumeDropConflict, sharedVolumeTopLevelEntries } from "@/lib/shared-volumes";
import {
	selectEnvironmentUploadOperation,
	selectIsChatEnvironmentStopped,
	useChatStore,
} from "@/stores/useChatStore";
import { useCloudSharedVolumeStore } from "@/stores/useCloudSharedVolumeStore";
import { useDeviceSharedVolumeStore } from "@/stores/useDeviceSharedVolumeStore";
import { useToastStore } from "@/stores/useToastStore";

/** 上書きしてよいかの確認 1 回分。 */
interface PendingOverwrite {
	count: number;
	resolve: (confirmed: boolean) => void;
}

/** 置き先。directory は根からのパス。 */
interface SharedVolumePlacement {
	environmentId: string;
	volumeId: string;
	directory: string;
	/** 呼び出し側の表示が今もこの環境か。 */
	isCurrent: () => boolean;
}

const REFRESH_FAILED = "共有ボリュームの状態を取り直せませんでした。";

/**
 * 共有ボリュームに PC のファイルを置く処理と、同じ名前のものがあるときの上書きの確認。デバイスとクラウドで共通。
 * environmentId は表示中の環境。refresh は置いた後 (失敗しても、途中まで置けていれば) 表示を取り直す。
 * createEmptyDirectory (根からのパス) が無ければ、落としたフォルダの中の空のフォルダは置かない。
 */
function useSharedVolumePlacement(
	environmentId: string | null,
	overwriteMessage: (count: number) => string,
	refresh: (placement: SharedVolumePlacement) => Promise<unknown>,
	createEmptyDirectory: ((placement: SharedVolumePlacement, path: string) => Promise<void>) | null,
) {
	const [pendingOverwrite, setPendingOverwrite] = useState<PendingOverwrite | null>(null);
	const pendingOverwriteRef = useRef<PendingOverwrite | null>(null);
	const mountedRef = useRef(false);

	const answerOverwrite = useCallback((pending: PendingOverwrite, confirmed: boolean) => {
		if (pendingOverwriteRef.current === pending) pendingOverwriteRef.current = null;
		setPendingOverwrite((current) => (current === pending ? null : current));
		pending.resolve(confirmed);
	}, []);

	// 確かめている間に閉じたか別の環境に移ったら上書きしない (待ったままだとアップロードの排他が外れない)。
	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
			pendingOverwriteRef.current?.resolve(false);
			pendingOverwriteRef.current = null;
			setPendingOverwrite(null);
		};
	}, [environmentId]);

	/**
	 * 同じ名前のものがあれば上書きしてよいか確かめる。無ければ確かめずに true。フォルダとファイルが食い違うものが
	 * あれば (上書きでは置き換えられないので) 知らせて false。
	 */
	const confirmOverwrite = useCallback(
		async (
			env: string,
			volumeId: string,
			dropped: SharedVolumeExistingPath[],
			isCurrent: () => boolean,
		): Promise<boolean> => {
			const existing = await existingSharedVolumePaths(
				env,
				volumeId,
				dropped.map((entry) => entry.path),
			);
			// 待つ間に閉じたか別の環境に移ったら、確かめずにやめる。
			if (!mountedRef.current || !isCurrent()) return false;
			const conflict = sharedVolumeDropConflict(dropped, existing);
			if (conflict !== null) {
				useToastStore.getState().show({ type: "error", message: conflict });
				return false;
			}
			if (existing.length === 0) return true;
			// 確認は 1 つずつ (排他があるので通常は重ならない)。
			if (pendingOverwriteRef.current !== null) return false;
			return new Promise<boolean>((resolve) => {
				const pending = { count: existing.length, resolve };
				pendingOverwriteRef.current = pending;
				setPendingOverwrite(pending);
			});
		},
		[],
	);

	/** 置くもの (置き先からの相対パス) を確かめる。確かめられなければ知らせてから投げる。 */
	const confirmPlacement = useCallback(
		async (placement: SharedVolumePlacement, relativePaths: string[]): Promise<boolean> => {
			const dropped = sharedVolumeTopLevelEntries(placement.directory, relativePaths);
			if (dropped.length === 0) return false;
			try {
				return await confirmOverwrite(
					placement.environmentId,
					placement.volumeId,
					dropped,
					placement.isCurrent,
				);
			} catch (err) {
				if (placement.isCurrent()) {
					useToastStore.getState().show({
						type: "error",
						message:
							err instanceof ApiStatusError
								? err.message
								: "ファイルを共有ボリュームに置けませんでした。",
					});
				}
				throw err;
			}
		},
		[confirmOverwrite],
	);

	const uploadOptions = useCallback(
		(placement: SharedVolumePlacement): EnvironmentUploadOptions => ({
			sharedVolumeId: placement.volumeId,
			...(createEmptyDirectory === null
				? { emptyDirectories: [] }
				: { createEmptyDirectory: (path: string) => createEmptyDirectory(placement, path) }),
			// 取り直せなくても置いたことは失敗にしない (別に知らせる)。
			refresh: async () => reportSharedVolumeFailure(refresh(placement), REFRESH_FAILED),
		}),
		[createEmptyDirectory, refresh],
	);

	/** OS から落としたファイル・フォルダを置く。DataTransfer は drop の同期フェーズの内に読む。 */
	const placeDroppedFiles = useCallback(
		(placement: SharedVolumePlacement, dataTransfer: DataTransfer) => {
			uploadDroppedFilesToEnvironment(placement.environmentId, dataTransfer, placement.directory, {
				isCurrent: placement.isCurrent,
				beforeUpload: (batch) =>
					confirmPlacement(placement, [
						...batch.files.map((file) => resolveUploadItemPath("", file)),
						// 末尾の / でフォルダとして確かめる。
						...(createEmptyDirectory === null
							? []
							: batch.emptyDirectories.map((directory) => `${directory}/`)),
					]),
				upload: uploadOptions(placement),
			});
		},
		[confirmPlacement, createEmptyDirectory, uploadOptions],
	);

	/** ファイル選択で選んだものを置く。 */
	const placePickedFiles = useCallback(
		(placement: SharedVolumePlacement, files: readonly File[]) =>
			uploadFilesToActiveEnvironment(placement.directory, files, {
				beforeUpload: async (env, isVisible) =>
					env === placement.environmentId &&
					placement.isCurrent() &&
					(await confirmPlacement(
						{ ...placement, isCurrent: () => placement.isCurrent() && isVisible() },
						files.map((file) => resolveUploadItemPath("", file)),
					)),
				upload: uploadOptions(placement),
			}),
		[confirmPlacement, uploadOptions],
	);

	/** ~ のツリーからドラッグしたもの (一番上のものだけ) を、~ に元を残してコピーする。 */
	const placeHomeEntries = useCallback(
		(placement: SharedVolumePlacement, payload: EnvironmentEntryPayload) => {
			const { environmentId: env, volumeId, directory, isCurrent } = placement;
			const topLevel = new Set(
				getTopLevelEnvironmentPaths(payload.entries.map((entry) => entry.path)),
			);
			const entries = payload.entries.filter((entry) => topLevel.has(entry.path));
			const seen = new Set<string>();
			const duplicate = entries.find((entry) => {
				if (seen.has(entry.name)) return true;
				seen.add(entry.name);
				return false;
			});
			if (duplicate !== undefined) {
				useToastStore.getState().show({
					type: "error",
					message: `同じ名前のものが複数あるため置けません: ${duplicate.name}`,
				});
				return;
			}
			const dropped = entries.map((entry) => ({
				path: directory === "" ? entry.name : `${directory}/${entry.name}`,
				directory: entry.entryType === "directory",
			}));
			const operationId = useChatStore.getState().beginEnvironmentUpload(env);
			if (operationId === null) return;
			const copy = async () => {
				let started = false;
				try {
					if (!(await confirmOverwrite(env, volumeId, dropped, isCurrent))) return;
					started = true;
					await withLoadingToast("共有ボリュームにコピーしています…", async () => {
						const { copyId } = await copyHomeEntriesToSharedVolume(
							env,
							volumeId,
							entries.map((entry) => entry.path),
							directory,
						);
						await waitForSharedVolumeHomeCopy(env, volumeId, copyId);
					});
					if (isCurrent()) {
						useToastStore
							.getState()
							.show({ type: "success", message: "共有ボリュームにコピーしました。" });
					}
				} finally {
					useChatStore.getState().endEnvironmentUpload(env, operationId);
					// 途中まで置けていることもあるので、失敗しても取り直す。
					if (started && isCurrent()) {
						reportSharedVolumeFailure(refresh(placement), REFRESH_FAILED);
					}
				}
			};
			reportSharedVolumeFailure(copy(), "共有ボリュームにコピーできませんでした。");
		},
		[confirmOverwrite, refresh],
	);

	const overwriteDialog = (
		<ConfirmDialog
			open={pendingOverwrite !== null}
			title="上書きする"
			message={overwriteMessage(pendingOverwrite?.count ?? 0)}
			confirmLabel="上書き"
			onConfirm={async () => {
				if (pendingOverwrite) answerOverwrite(pendingOverwrite, true);
			}}
			onCancel={() => {
				if (pendingOverwrite) answerOverwrite(pendingOverwrite, false);
			}}
		/>
	);

	return { placeDroppedFiles, placePickedFiles, placeHomeEntries, overwriteDialog };
}

const deviceOverwriteMessage = (count: number) =>
	`${count} 件は同じ名前のものが既にあります。上書きすると、反映したときに共有ボリュームのものも上書きされます。`;

// 置けた分を未反映として出す。
const refreshDeviceCopy = () =>
	useDeviceSharedVolumeStore.getState().refresh({ scan: true, relist: "none" });

/**
 * デバイスの環境の共有ボリュームの行に落としたもの (PC のファイル・~ のファイルとフォルダ) を、手元の写しに置く。同じ名前の
 * ものがあれば上書きしてよいか確かめ、置いたら手元の変更を数え直して未反映として出す。置いている間は環境のアップロードの
 * 排他を取る (~ と共有し、その間は行が受けない)。
 */
export function useSharedVolumeDrop() {
	const canManageAgents = useHasPermission("manageAgents");
	const environmentId = useDeviceSharedVolumeStore((s) => s.environmentId);
	const running = useDeviceSharedVolumeStore((s) => s.overview?.running === true);
	const uploading = useChatStore(
		(s) => selectEnvironmentUploadOperation(s, environmentId) !== null,
	);
	const { placeDroppedFiles, placeHomeEntries, overwriteDialog } = useSharedVolumePlacement(
		environmentId,
		deviceOverwriteMessage,
		refreshDeviceCopy,
		null,
	);

	/** 行に置けるなら置くディレクトリ (根からのパス)。 */
	const dropDirectory = useCallback(
		(row: DeviceSharedVolumeEntryRow): string | null =>
			canManageAgents && running && !uploading ? deviceSharedVolumeDropDirectory(row) : null,
		[canManageAgents, running, uploading],
	);

	/** OS から落としたファイル・フォルダを置く。DataTransfer は drop の同期フェーズの内に読む。 */
	const dropOsFiles = useCallback(
		(row: DeviceSharedVolumeEntryRow, dataTransfer: DataTransfer) => {
			const directory = dropDirectory(row);
			if (environmentId === null || directory === null) return;
			const env = environmentId;
			placeDroppedFiles(
				{
					environmentId: env,
					volumeId: row.volumeId,
					directory,
					isCurrent: () => useDeviceSharedVolumeStore.getState().environmentId === env,
				},
				dataTransfer,
			);
		},
		[dropDirectory, environmentId, placeDroppedFiles],
	);

	/** ~ のツリーからドラッグしたものを写しにコピーする (~ には元を残す)。 */
	const dropHomeEntries = useCallback(
		(row: DeviceSharedVolumeEntryRow, payload: EnvironmentEntryPayload) => {
			const directory = dropDirectory(row);
			if (environmentId === null || directory === null) return;
			const env = environmentId;
			placeHomeEntries(
				{
					environmentId: env,
					volumeId: row.volumeId,
					directory,
					isCurrent: () => useDeviceSharedVolumeStore.getState().environmentId === env,
				},
				payload,
			);
		},
		[dropDirectory, environmentId, placeHomeEntries],
	);

	return { dropDirectory, dropOsFiles, dropHomeEntries, overwriteDialog };
}

export const CLOUD_OVERWRITE_MESSAGE =
	"共有ボリュームの同じ名前のファイルを上書きします。このボリュームを使う他のエージェントにもすぐ反映されます。";

const cloudOverwriteMessage = () => CLOUD_OVERWRITE_MESSAGE;

const reloadCloudDirectory = ({ volumeId, directory }: SharedVolumePlacement) =>
	useCloudSharedVolumeStore.getState().reloadDirectory(volumeId, directory);

const createCloudEmptyDirectory = (
	{ environmentId, volumeId }: SharedVolumePlacement,
	path: string,
) => ensureCloudSharedVolumeFolder(environmentId, volumeId, path);

/**
 * クラウドの環境の共有ボリュームの行に、PC のファイル・フォルダを落とすか右クリックのアップロードで選んで、mount された
 * 共有ボリュームに直接置く。~ のツリーからドラッグしたものはコピーする。同じ名前のものがあれば上書きしてよいか確かめ、
 * 置いたらそのディレクトリを取り直す。
 */
export function useCloudSharedVolumeDrop() {
	const canManageAgents = useHasPermission("manageAgents");
	const environmentId = useCloudSharedVolumeStore((s) => s.environmentId);
	const running = !useChatStore(selectIsChatEnvironmentStopped);
	const uploading = useChatStore(
		(s) => selectEnvironmentUploadOperation(s, environmentId) !== null,
	);
	const { placeDroppedFiles, placePickedFiles, placeHomeEntries, overwriteDialog } =
		useSharedVolumePlacement(
			environmentId,
			cloudOverwriteMessage,
			reloadCloudDirectory,
			createCloudEmptyDirectory,
		);

	const placement = useCallback(
		(
			row: Pick<CloudSharedVolumeEntryRow, "volumeId">,
			directory: string,
		): SharedVolumePlacement | null => {
			if (environmentId === null) return null;
			const env = environmentId;
			return {
				environmentId: env,
				volumeId: row.volumeId,
				directory,
				isCurrent: () => useCloudSharedVolumeStore.getState().environmentId === env,
			};
		},
		[environmentId],
	);

	/** 右クリックのアップロードで置くディレクトリ (根からのパス)。置けない行なら null。 */
	const uploadDirectory = useCallback(
		(row: CloudSharedVolumeEntryRow): string | null =>
			canManageAgents && running ? cloudSharedVolumeDropDirectory(row) : null,
		[canManageAgents, running],
	);

	/** 行に落とせるなら置くディレクトリ (根からのパス)。 */
	const dropDirectory = useCallback(
		(row: CloudSharedVolumeEntryRow): string | null => (uploading ? null : uploadDirectory(row)),
		[uploadDirectory, uploading],
	);

	/** OS から落としたファイル・フォルダを置く。DataTransfer は drop の同期フェーズの内に読む。 */
	const dropOsFiles = useCallback(
		(row: CloudSharedVolumeEntryRow, dataTransfer: DataTransfer) => {
			const directory = dropDirectory(row);
			const target = directory === null ? null : placement(row, directory);
			if (target !== null) placeDroppedFiles(target, dataTransfer);
		},
		[dropDirectory, placeDroppedFiles, placement],
	);

	/** ~ のツリーからドラッグしたものをコピーする (~ には元を残す)。 */
	const dropHomeEntries = useCallback(
		(row: CloudSharedVolumeEntryRow, payload: EnvironmentEntryPayload) => {
			const directory = dropDirectory(row);
			const target = directory === null ? null : placement(row, directory);
			if (target !== null) placeHomeEntries(target, payload);
		},
		[dropDirectory, placeHomeEntries, placement],
	);

	/** ファイル選択で選んだものを、ボリュームの directory (根からのパス) に置く。 */
	const uploadPickedFilesTo = useCallback(
		async (volumeId: string, directory: string, files: readonly File[]) => {
			const target = canManageAgents && running ? placement({ volumeId }, directory) : null;
			if (target !== null) await placePickedFiles(target, files);
		},
		[canManageAgents, placePickedFiles, placement, running],
	);

	/** ファイル選択で選んだものを行に置く。 */
	const uploadPickedFiles = useCallback(
		async (row: CloudSharedVolumeEntryRow, files: readonly File[]) => {
			const directory = uploadDirectory(row);
			if (directory !== null) await uploadPickedFilesTo(row.volumeId, directory, files);
		},
		[uploadDirectory, uploadPickedFilesTo],
	);

	return {
		dropDirectory,
		dropOsFiles,
		dropHomeEntries,
		uploadDirectory,
		uploadPickedFiles,
		uploadPickedFilesTo,
		overwriteDialog,
	};
}
